/* eslint-disable import/no-extraneous-dependencies */

import { Button, Tooltip } from '@mui/material'
import { ReactNode, useEffect, useRef, useState } from 'react'
import Fade from '@mui/material/Fade'
import './ColorPicker.scss'
import IconButton from '@/shared/components/IconButton/IconButton'
import ColorInputs, {
  ColorInputsHandle
} from '@/shared/components/ColorInputs/ColorInputs'
import Saturation from '@uiw/react-color-saturation'
import Hue from '@uiw/react-color-hue'
import Alpha from '@uiw/react-color-alpha'

import { useClickOutsideWithExceptions } from '@/shared/hooks/useClickOutsideWithExceptions'
import { hexToHsva, hsvaToRgba } from '@uiw/color-convert'

import { useColorStore } from '@/features/mobile-app/store/useColorStore'
import {
  colorPickerButtonStyles,
  popperStyles,
  tooltipStyles
} from './ColorPicker.styles'

type HSVA = {
  h: number
  s: number
  v: number
  a: number
}

interface ColorPickerProps {
  color: HSVA
  setColor: (color: HSVA) => void
  /** Plain text in most places; a node where the tile carries more than a name */
  label: ReactNode
  showAlpha?: boolean
  /**
   * Fires when the user has FINISHED picking, pointer released on the wheel or
   * a slider, a value typed into the inputs, a colour taken with the dropper.
   *
   * `setColor` keeps firing throughout a drag so a preview can follow the
   * pointer; that is the wrong moment for anything expensive. An owner that
   * writes to a server should do it from here instead: a slow drag across the
   * wheel is otherwise a stream of requests, one per 150ms tick.
   */
  onChangeCommitted?: (color: HSVA) => void
}

const hsvaToRgbaString = (hsva: HSVA) => {
  const { r, g, b, a } = hsvaToRgba(hsva)
  return `rgba(${r}, ${g}, ${b}, ${a})`
}

function CustomColorPicker({
  color: initialColor,
  setColor,
  showAlpha = false,
  onChangeCommitted
}: {
  color: HSVA
  setColor: (color: HSVA) => void
  showAlpha?: boolean
  onChangeCommitted?: (color: HSVA) => void
}) {
  const { applyColors } = useColorStore()
  const [color, setLocalColor] = useState<HSVA>(initialColor)
  const [error, setError] = useState<string | null>(null)
  const [isInputFocused, setIsInputFocused] = useState(false)
  const lastAppliedColorRef = useRef<HSVA>(initialColor)

  const timeoutId = useRef<NodeJS.Timeout | null>(null)
  const inputRef = useRef<ColorInputsHandle>(null)
  /**
   * Whether a pointer drag is in progress, and the newest colour it has
   * produced. The release is caught on `window` (see the effect below), which
   * cannot read React state, so the value it commits is kept here.
   */
  const isDraggingRef = useRef(false)
  const latestColorRef = useRef<HSVA>(initialColor)

  useEffect(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current)
    }
  }, [])

  useEffect(() => {
    setLocalColor(initialColor)
    if (!isDraggingRef.current) {
      latestColorRef.current = initialColor
    }
  }, [initialColor])

  const updateStoreColor = (newColor: HSVA, committed = false) => {
    const last = lastAppliedColorRef.current
    const isSameColor =
      newColor.h === last.h &&
      newColor.s === last.s &&
      newColor.v === last.v &&
      newColor.a === last.a

    if (!isSameColor) {
      lastAppliedColorRef.current = newColor
      setColor(newColor)
      applyColors()
    }

    // Announced even when the value did not move. The commit is about the
    // interaction ending, not about the colour changing: a drag whose 150ms
    // tick already pushed the final value would otherwise return above and the
    // owner would never hear that the user let go.
    if (committed) {
      onChangeCommitted?.(newColor)
    }
  }

  const handleOnChange = (newHsva: HSVA) => {
    setLocalColor(newHsva)
    latestColorRef.current = newHsva
    if (timeoutId.current) clearTimeout(timeoutId.current)
    timeoutId.current = setTimeout(() => {
      // Committed only when no pointer is down. A drag has a release to wait
      // for; keyboard stepping on the focused square or slider does not, and
      // without this it could never be committed at all.
      updateStoreColor(newHsva, !isDraggingRef.current)
    }, 150)
  }

  const beginDrag = () => {
    inputRef.current?.finalizeInput()
    isDraggingRef.current = true
  }

  const finishDrag = () => {
    if (!isDraggingRef.current) return
    isDraggingRef.current = false
    if (timeoutId.current) clearTimeout(timeoutId.current)
    updateStoreColor(latestColorRef.current, true)
  }

  // Held in a ref so the window listener can be registered once and still call
  // the current closure.
  const finishDragRef = useRef(finishDrag)
  finishDragRef.current = finishDrag

  /**
   * The release is caught on `window`, not on the square or the slider.
   *
   * @uiw's drag helper starts on the element's mousedown but then tracks the
   * gesture with window-level `mousemove`/`mouseup` and never takes pointer
   * capture, so dragging to a corner or past the end of the hue bar, which is
   * how anyone reaches a pure colour, releases OUTSIDE the element. A React
   * `onPointerUp` on the element never runs there, and the pick was silently
   * dropped: visible in the preview, never sent anywhere.
   *
   * Same two events the helper itself ends on, so the two always agree.
   */
  useEffect(() => {
    const handleRelease = () => finishDragRef.current()

    window.addEventListener('mouseup', handleRelease)
    window.addEventListener('touchend', handleRelease)

    return () => {
      window.removeEventListener('mouseup', handleRelease)
      window.removeEventListener('touchend', handleRelease)
    }
  }, [])

  const handleInputChangeFromColorInputs = (
    newColor: HSVA,
    apply?: boolean
  ) => {
    setLocalColor(newColor)
    latestColorRef.current = newColor
    // `apply` marks a finished entry. Enter, blur, a complete hex. Every other
    // call is a keystroke mid-typing, which the preview should follow but which
    // is no more a decision than a half-finished drag.
    updateStoreColor(newColor, apply === true)
  }

  const isEyeDropperSupported = 'EyeDropper' in window

  const openEyeDropper = async () => {
    if (!isEyeDropperSupported) {
      setError('Your browser does not support the EyeDropper API.')
      return
    }

    const eyeDropper = new (
      window as unknown as {
        EyeDropper: new () => { open: () => Promise<{ sRGBHex: string }> }
      }
    ).EyeDropper()

    try {
      const result = await eyeDropper.open()
      const newColor = hexToHsva(result.sRGBHex)
      const updatedColor = {
        h: newColor.h,
        s: newColor.s,
        v: newColor.v,
        a: newColor.a
      }
      setLocalColor(updatedColor)
      latestColorRef.current = updatedColor
      updateStoreColor(updatedColor, true)
      setError(null)
    } catch (err) {
      setError('EyeDropper was canceled or failed.')
    }
  }

  return (
    <div data-click-outside-exception className="colorPickerContainer">
      <Saturation
        hsva={color}
        onPointerDown={beginDrag}
        onChange={newHsva => handleOnChange(newHsva)}
        className="colorPickerSaturation"
      />
      <div className="colorPickerOptionsWrapper">
        <IconButton
          onClick={openEyeDropper}
          color="primary"
          icon="las la-eye-dropper"
          disabled={!isEyeDropperSupported || error !== null}
        />
        <div className="colorPickerSlidersContainer">
          <Hue
            hue={color.h}
            onPointerDown={beginDrag}
            onChange={newHue => handleOnChange({ ...color, h: newHue.h })}
            className="colorPickerHue"
          />
          {showAlpha && (
            <Alpha
              hsva={color}
              onPointerDown={beginDrag}
              onChange={newAlpha => handleOnChange({ ...color, a: newAlpha.a })}
              className="colorPickerAlpha"
            />
          )}
        </div>
      </div>

      <div className="colorPickerOptionsContainer">
        <div
          className="colorPickerColorTile"
          style={{
            background: hsvaToRgbaString(color)
          }}
        />
        <ColorInputs
          ref={inputRef}
          color={color}
          onChange={handleInputChangeFromColorInputs}
          isInputFocused={isInputFocused}
          setIsInputFocused={setIsInputFocused}
        />
      </div>
    </div>
  )
}

function ColorPicker(props: ColorPickerProps) {
  const { label, color, setColor, showAlpha, onChangeCommitted } = props
  const [openColorPicker, setOpenColorPicker] = useState(false)

  const toggleOpenColorPicker = () => setOpenColorPicker(!openColorPicker)

  const buttonRef = useRef<HTMLDivElement>(null)
  const popperRef = useRef<HTMLDivElement>(null)

  useClickOutsideWithExceptions({
    refs: [buttonRef, popperRef],
    exceptionSelectors: ['#color-format-select-listbox'],
    onClickOutside: () => setOpenColorPicker(false),
    disabled: !openColorPicker
  })

  const picker = (
    <CustomColorPicker
      color={color}
      setColor={setColor}
      showAlpha={showAlpha}
      onChangeCommitted={onChangeCommitted}
    />
  )

  return (
    <div className="colorPickerWrapper" ref={buttonRef}>
      <Tooltip
        title={picker}
        open={openColorPicker}
        slots={{
          transition: Fade
        }}
        placement="bottom-start"
        slotProps={{
          tooltip: {
            sx: { ...tooltipStyles }
          },
          popper: {
            sx: { popperStyles },
            ref: popperRef
          }
        }}
      >
        <Button
          variant="contained"
          color="primary"
          disableRipple
          onClick={toggleOpenColorPicker}
          sx={colorPickerButtonStyles}
        >
          <div className="body-bold-medium colorPickerLabel">{label}</div>
          <div className="colorPickerButtonContainer">
            <div
              className="colorTile"
              style={{
                background: hsvaToRgbaString(color)
              }}
            />
          </div>
        </Button>
      </Tooltip>
    </div>
  )
}

export default ColorPicker
